import React, { useState } from "react";
import PropTypes from "prop-types";
import Image from "../../../../image/image";
import Link from "../../../../link/link";
import { restHostBackend } from "@default/js/config";
import CustomAnimation from "@default/general-components/custom-animation";
import SpotifyButtonListen from "@default/js/participants/components/spotify-widget/spotify-button-listen";
import SpotifyButtonIdle from "@default/js/participants/components/spotify-widget/spotify-button-idle";
import LazyLoad from "react-lazyload";
import classNames from "classnames";
import TypewriterAnimation from "@default/general-components/typewriter-animation";

const DossierElement = ({ content, active, highlighted }) => {
  const [muted, setMuted] = useState(true);

  const imageData =
    content.fieldImage?.fieldMediaImage ||
    content.fieldReferencedNodes?.fieldImage?.fieldMediaImage;

  const title = content.fieldHeading || content.fieldReferencedNodes?.title;
  const text = content.fieldText || content.fieldReferencedNodes?.fieldText;

  const videoUrl =
    content.fieldVideoFile &&
    `${restHostBackend}/${content.fieldVideoFile?.mediaFileUrl?.path}`;

  const toggleMute = () => {
    setMuted(!muted);
  };

  const headlineClassNames = classNames({
    headline: true,
    "headline-s": highlighted,
    "body-m": !highlighted,
  });

  const teaserContent = (
    <>
      <LazyLoad offset={500}>
        {imageData && (
          <div className="main-image fading-image-hover-wrapper">
            <Image data={imageData} style={imageData.style32} />
            {videoUrl && (
              <CustomAnimation type={"appear"} isVisible={active}>
                <video
                  src={videoUrl}
                  autoPlay={true}
                  loop={true}
                  muted={muted}
                  playsInline={true}
                  aria-label={"Video"}
                />
                <button
                  className="toggle-spotify"
                  onClick={() => toggleMute()}
                  aria-label={"Play Video"}
                >
                  <CustomAnimation isVisible={!muted} type={"appear"}>
                    <SpotifyButtonListen />
                  </CustomAnimation>
                  <CustomAnimation isVisible={muted} type={"appear"}>
                    <SpotifyButtonIdle />
                  </CustomAnimation>
                </button>
              </CustomAnimation>
            )}
          </div>
        )}
      </LazyLoad>
      <div className="teaser-body">
        {title && <h3 className={headlineClassNames}>{title}</h3>}
        {text && (
          <div
            className="text body-m"
            dangerouslySetInnerHTML={{ __html: text }}
          />
        )}
        {content.fieldDossierLink && (
          <>
            {content.fieldDossierLink.title ? (
              <span className="read-more typewriter-anchor">
                <TypewriterAnimation>
                  {content.fieldDossierLink.title}
                </TypewriterAnimation>
              </span>
            ) : (
              <span className="read-more typewriter-anchor">
                <TypewriterAnimation>learn more</TypewriterAnimation>
              </span>
            )}
          </>
        )}
      </div>
    </>
  );

  const dossierElementClassNames = classNames({
    "dossier-element": true,
    highlighted: highlighted,
    standard: !highlighted,
  });

  return (
    <div className={dossierElementClassNames}>
      {content.fieldDossierLink ? (
        <Link
          link={content.fieldDossierLink}
          ariaLabel={title}
          tabIndex={active ? "0" : "-1"}
          role="group"
          aria-roledescription="slide"
          aria-labelledby={title}
          aria-current={active ? "true" : undefined}
          className="action-element"
        >
          {teaserContent}
        </Link>
      ) : (
        <article
          aria-label={title}
          tabIndex={active ? "0" : "-1"}
          role="group"
          aria-roledescription="slide"
          aria-labelledby={title}
          aria-current={active ? "true" : undefined}
        >
          {teaserContent}
        </article>
      )}
    </div>
  );
};

DossierElement.propTypes = {
  active: PropTypes.bool,
  highlighted: PropTypes.bool,
  content: PropTypes.shape({
    fieldImage: PropTypes.object,
    fieldText: PropTypes.string,
    fieldTitle: PropTypes.string,
    fieldReferencedNodes: PropTypes.shape({
      title: PropTypes.string,
      fieldText: PropTypes.string,
      fieldImage: PropTypes.shape({
        fieldMediaImage: PropTypes.shape({
          alt: PropTypes.string,
          title: PropTypes.string,
          style: PropTypes.shape({
            url: PropTypes.string,
          }),
        }),
      }),
      url: PropTypes.shape({
        path: PropTypes.string,
      }),
    }),
  }),
};

export default DossierElement;
